import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/useAuthStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// The one HTTP client for the app. Every service and page goes through this
// instance so they all share the auth header and the token refresh below.
// Do not create another axios instance or call fetch directly: anything that
// bypasses this client stops working once the 15 minute access token expires.
export const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Endpoints where a 401 means "bad credentials or bad token in the body", not
// "access token expired". Refreshing and retrying these would be pointless,
// and on the sign-in page it would reload the page and wipe the error message.
const NO_REFRESH_PATHS = [
    '/auth/login',
    '/auth/register',
    '/auth/refresh',
    '/auth/activate',
    '/auth/request-password-reset',
    '/auth/reset-password',
];

function isNoRefreshPath(url: string | undefined): boolean {
    if (!url) return false;
    const path = url.startsWith(API_BASE_URL) ? url.slice(API_BASE_URL.length) : url;
    return NO_REFRESH_PATHS.some((p) => path === p || path.startsWith(`${p}?`));
}

// Raised when the session itself is gone (no refresh token, or the backend
// rejected it), as opposed to the refresh call failing for a transient reason.
class SessionExpiredError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SessionExpiredError';
    }
}

// Single-flight token refresh: coalesce concurrent 401s into one /auth/refresh
// call. The backend rotates the refresh token on every use, so firing several
// refreshes at once would invalidate each other and bounce the user to sign-in.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
        throw new SessionExpiredError('No refresh token');
    }

    let response;
    try {
        // Plain axios, not `api`, so a 401 from the refresh call can never
        // re-enter this interceptor and loop.
        response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {},
            {
                timeout: 30000,
                headers: {
                    Authorization: `Bearer ${refreshToken}`,
                    'Content-Type': 'application/json',
                },
            },
        );
    } catch (error) {
        const status = (error as AxiosError).response?.status;
        if (status === 401 || status === 403) {
            throw new SessionExpiredError('Refresh token rejected');
        }
        // Network failure or server error: the session may still be valid.
        throw error;
    }

    const { accessToken, refreshToken: newRefreshToken } = response.data;
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', newRefreshToken);
    return accessToken;
}

const SIGN_IN_PATH = '/';

function endSession() {
    // Clears both tokens and the persisted zustand auth state.
    useAuthStore.getState().logout();
    // The sign-in page lives at '/'. Skip the reload when already there.
    if (window.location.pathname !== SIGN_IN_PATH) {
        window.location.href = SIGN_IN_PATH;
    }
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

// Response interceptor for token refresh
api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as RetriableConfig | undefined;

        if (
            error.response?.status !== 401 ||
            !originalRequest ||
            originalRequest._retry ||
            isNoRefreshPath(originalRequest.url)
        ) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        // A refresh may already have finished while this request was in flight
        // with the old token. If so, retry with the new token rather than
        // spending another refresh.
        const sentToken = String(originalRequest.headers?.Authorization ?? '').replace(
            /^Bearer /,
            '',
        );
        const storedToken = localStorage.getItem('accessToken');
        if (!refreshPromise && storedToken && sentToken && storedToken !== sentToken) {
            originalRequest.headers.Authorization = `Bearer ${storedToken}`;
            return api(originalRequest);
        }

        try {
            // Reuse an in-flight refresh if one is already running.
            if (!refreshPromise) {
                refreshPromise = refreshAccessToken().finally(() => {
                    refreshPromise = null;
                });
            }
            const accessToken = await refreshPromise;

            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
        } catch (refreshError) {
            if (refreshError instanceof SessionExpiredError) {
                endSession();
                return Promise.reject(refreshError);
            }
            // Transient refresh failure: surface the original 401 and keep
            // the session so the next request can try again.
            return Promise.reject(error);
        }
    },
);

export default api;
